// Firebase Client SDK Configuration (Frontend)
// This file initializes Firebase for the React app

import { initializeApp } from "firebase/app";
import { getAnalytics, isSupported } from "firebase/analytics";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { getFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator, httpsCallable } from "firebase/functions";
import { getMessaging, getToken, onMessage, isSupported as isMessagingSupported, Messaging } from "firebase/messaging";

// Firebase configuration loaded from environment variables.
// These are public client-side keys (not secrets), but we centralize them
// in .env to avoid duplication and make per-environment config easy.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyDE-tr9BRbuoNZfjpYZQR6mAR7OPiTpREQ",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "fash-70b87.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "fash-70b87",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "fash-70b87.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "715391976788",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:715391976788:web:d83ce153dab1316c0974c1",
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || "G-K07XCYZVQR",
};

// VAPID key for FCM web push (generate in Firebase Console > Cloud Messaging > Web Push certificates)
// This is a public key — safe to include in client code
const RAW_VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';

/**
 * A real web push certificate is a ~87-char base64url P-256 key starting with "B".
 * Placeholder values (e.g. the "your_vapid_key" stub in .env.example) must be
 * rejected: they are truthy, so they would otherwise be passed to getToken()
 * and fail, while also suppressing the admin-configured fallback.
 */
export function isPlausibleVapidKey(key: unknown): key is string {
  return typeof key === 'string' && key.length >= 80 && /^B[A-Za-z0-9_-]+$/.test(key);
}

export const VAPID_KEY = isPlausibleVapidKey(RAW_VAPID_KEY) ? RAW_VAPID_KEY : '';

if (RAW_VAPID_KEY && !VAPID_KEY) {
  console.warn(
    'VITE_FIREBASE_VAPID_KEY is set to an invalid/placeholder value and will be ignored. ' +
    'Generate a Web Push certificate in Firebase Console → Cloud Messaging.'
  );
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Analytics (only in browser)
let analytics = null;
if (typeof window !== 'undefined') {
  isSupported().then(supported => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  });
}

// Firestore database ID. FASH is a standalone app with its own dedicated
// named database 'fashdb' (project fash-70b87). Override via env if needed.
export const FIRESTORE_DATABASE_ID =
  import.meta.env.VITE_FIRESTORE_DATABASE_ID || 'fashdb';

// Initialize Firebase services
const auth = getAuth(app);
const db = getFirestore(app, FIRESTORE_DATABASE_ID);
const storage = getStorage(app);
const functions = getFunctions(app);

// Initialize Firebase Cloud Messaging (only in browser with service worker support)
let messaging: Messaging | null = null;

async function initializeMessaging(): Promise<Messaging | null> {
  if (typeof window === 'undefined') return null;
  
  try {
    const supported = await isMessagingSupported();
    if (supported) {
      messaging = getMessaging(app);
      return messaging;
    }
  } catch (error) {
    console.warn('Firebase Messaging not supported in this browser:', error);
  }
  return null;
}

// Initialize messaging lazily
const messagingPromise = initializeMessaging();

let cachedVapidKey: string | null = null;
let vapidLookup: Promise<string | null> | null = null;

/**
 * Resolve the FCM VAPID key from the build env or admin-configured backend fallback.
 *
 * The backend callable requires an authenticated caller, so failures are never
 * cached — a lookup before sign-in must not permanently mark push as unconfigured.
 */
export async function resolveVapidKey(): Promise<string | null> {
  if (VAPID_KEY) return VAPID_KEY;
  if (cachedVapidKey) return cachedVapidKey;
  if (vapidLookup) return vapidLookup;

  vapidLookup = (async () => {
    try {
      const getWebPushConfig = httpsCallable(functions, 'getWebPushConfig');
      const result = await getWebPushConfig({});
      const data = result.data as { vapidKey?: string | null; configured?: boolean };
      cachedVapidKey = isPlausibleVapidKey(data.vapidKey) ? data.vapidKey : null;
      return cachedVapidKey;
    } catch (error) {
      console.warn('Failed to resolve VAPID key from backend:', error);
      return null;
    } finally {
      vapidLookup = null;
    }
  })();

  return vapidLookup;
}

/**
 * Read the FCM token for an already-granted permission without prompting.
 * Used to deactivate the device token when a user turns push off after a reload.
 */
export async function getExistingPushToken(): Promise<string | null> {
  try {
    if (getPushPermissionStatus() !== 'granted') return null;

    const msg = await messagingPromise;
    const vapidKey = await resolveVapidKey();
    if (!msg || !vapidKey) return null;

    const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    if (!registration) return null;

    return await getToken(msg, { vapidKey, serviceWorkerRegistration: registration });
  } catch (error) {
    console.warn('Could not read existing push token:', error);
    return null;
  }
}

/**
 * Check whether this browser can use Firebase web push (service worker + secure context).
 */
export async function isPushMessagingSupported(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window)) return false;
  if (!window.isSecureContext) return false;

  try {
    return await isMessagingSupported();
  } catch {
    return false;
  }
}

/**
 * Request push notification permission and get FCM token.
 * Returns the FCM token string, or null if denied/unsupported.
 */
export async function requestPushPermission(): Promise<string | null> {
  try {
    const supported = await isPushMessagingSupported();
    if (!supported) {
      console.warn('Firebase Messaging not available in this browser');
      return null;
    }

    const msg = await messagingPromise;
    if (!msg) {
      console.warn('Firebase Messaging not available');
      return null;
    }

    // Request browser notification permission
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Push notification permission denied');
      return null;
    }

    const vapidKey = await resolveVapidKey();
    if (!vapidKey) {
      console.warn('VAPID key is not configured. Push notifications will not work.');
      return null;
    }

    // Register service worker and wait for it to fully activate
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await navigator.serviceWorker.ready;

    const token = await getToken(msg, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      console.log('FCM token obtained');
      return token;
    }

    console.warn('No FCM token received');
    return null;
  } catch (error) {
    console.error('Failed to get push notification token:', error);
    return null;
  }
}

/**
 * Listen for foreground push messages.
 *
 * Waits for messaging initialization to finish (it's async), so callers can
 * subscribe immediately on mount without racing `messagingPromise`.
 * Always returns an unsubscribe function.
 */
export function onForegroundMessage(callback: (payload: any) => void): () => void {
  let cancelled = false;
  let unsubscribe: (() => void) | null = null;

  messagingPromise.then((msg) => {
    if (!msg || cancelled) return;
    unsubscribe = onMessage(msg, (payload) => {
      console.log('Foreground message received:', payload);
      callback(payload);
    });
  });

  return () => {
    cancelled = true;
    if (unsubscribe) unsubscribe();
  };
}

/**
 * Check if push notifications are currently supported and permitted.
 */
export function getPushPermissionStatus(): 'granted' | 'denied' | 'default' | 'unsupported' {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

// Connect to emulators in development
if (import.meta.env.MODE === 'development' && import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
  console.log('Connecting to Firebase Emulators...');
  
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

export { app, analytics, auth, db, storage, functions, messaging };

